import axios from 'axios'; 

// Create axios instance with enhanced configuration
const axiosInstance = axios.create({
  baseURL: 'https://api.talkconnect.live',
  timeout: 10000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Global state for retry mechanism
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Set default auth header if token exists
const token = localStorage.getItem('authToken');
if (token) {
  axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

// Enhanced request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    // Get token for each request in case it changed
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    // Add timestamp to prevent caching
    if (config.method === 'get') {
      config.params = {
        ...config.params,
        _t: Date.now()
      };
    }

     // Safely check URLs for user state endpoints
     const url = config.url || '';
     if (url.includes('/user/set-active') || url.includes('/user/set-available')) {
       config.data = {
         ...config.data,
         deviceInfo: {
           deviceType: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
           browser: navigator.userAgent,
           platform: navigator.platform,
           timestamp: Date.now()
         }
       };
     }
 
     return config;
  },
  (error) => {
    console.error("Request error:", error);
    return Promise.reject(error);
  }
);

// Enhanced response interceptor
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle network errors
    if (!error.response) {
      console.error("Network error:", error);
      return Promise.reject({
        ...error,
        message: 'Network error. Please check your internet connection.'
      });
    }

    // Handle rate limiting
    if (error.response.status === 429) {
      const retryAfter = error.response.headers['retry-after'] || 30;
      return new Promise(resolve => {
        setTimeout(() => {
          resolve(axiosInstance(originalRequest));
        }, retryAfter * 1000);
      });
    }

    // Handle authentication errors
    if (error.response.status === 401) {
      if (error.response.data?.code === 'TOKEN_EXPIRED') {
        if (!isRefreshing) {
          isRefreshing = true;

          try {
            // Handle token refresh or logout
            localStorage.removeItem('authToken');
            localStorage.removeItem('userRole');
            localStorage.removeItem('userId');
            
            // Clear any active socket connections
            if (window.socket) {
              window.socket.disconnect();
            }

            window.location.href = '/login';
            return Promise.reject(error);
          } catch (refreshError) {
            processQueue(refreshError, null);
            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
          }
        }

        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return axiosInstance(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      // Other authentication errors
      if (error.response.data?.code === 'INVALID_TOKEN' || 
          error.response.data?.code === 'USER_NOT_FOUND') {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userRole');
        localStorage.removeItem('userId');
        window.location.href = '/login';
      }
    }

    // Handle server errors
    if (error.response.status >= 500) {
      console.error("Server error:", error.response.data);
      return Promise.reject({
        ...error,
        message: 'Server error. Please try again later.'
      });
    }

    return Promise.reject(error);
  }
);

// API endpoints definition
export const api = {
  auth: {
    login: '/api/auth/login',
    register: '/api/auth/register',
    userInfo: '/api/auth/user/info',
    updateInfo: '/api/auth/user/update-info',
    updateRole: '/api/auth/user/role',
    logout: '/api/auth/logout',
    // New endpoints for user state
    setActive: '/api/auth/user/set-active',
    setAvailable: '/api/auth/user/set-available',
    setUnavailable: '/api/auth/user/set-unavailable',
    updateDeviceInfo: '/api/auth/user/update-device-info',
    getActiveStatus: '/api/auth/user/active-status',
    checkUsername: (username) => `/api/auth/check-username/${username}`
  },
  call: {
    token: '/api/call/token',
    findMatch: (role) => `/api/call/find-match/${role}`,
    rateListener: '/api/call/rate-listener',
    activeCall: '/api/call/active-call',
    endCall: '/api/call/end-call',
    validateState: '/api/call/validate-state',
    health: '/api/call/health'
  }
};

// Helper methods for common API operations
export const apiHelpers = {
  setUserActive: async (deviceInfo) => {
    return axiosInstance.post(api.auth.setActive, { deviceInfo });
  },
  setUserAvailable: async (matchingPreferences) => {
    return axiosInstance.post(api.auth.setAvailable, { matchingPreferences });
  },
  setUserUnavailable: async () => {
    return axiosInstance.post(api.auth.setUnavailable);
  },
  updateDeviceInfo: async (deviceInfo) => {
    return axiosInstance.post(api.auth.updateDeviceInfo, { deviceInfo });
  },
  checkActiveStatus: async () => {
    return axiosInstance.get(api.auth.getActiveStatus);
  },
  validateCallState: async (channelName) => {
    return axiosInstance.get(api.call.validateState, { channelName });
  }
};

export default axiosInstance;